import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Store, BadgeCheck, ChevronRight } from 'lucide-react';
import ProductCard from './ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Section "MPStore Recommendation Store".
 * Menampilkan merchant whitelist beserta produknya langsung.
 * Muncul di beranda untuk semua user (data diambil dari lokasi toko sendiri).
 */
export const RecommendationStoreSection = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchFeatured = async () => {
      try {
        const res = await axios.get(`${API}/whitelist/featured`);
        if (active) setStores(res.data.stores || []);
      } catch (error) {
        console.error('Error fetching featured stores:', error);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchFeatured();
    return () => { active = false; };
  }, []);

  // Jangan render apa-apa kalau belum ada / tidak ada toko whitelist
  if (loading || stores.length === 0) return null;

  return (
    <div className="mb-12" data-testid="recommendation-store-section">
      {/* Section Title */}
      <div className="flex items-center gap-2 mb-4">
        <BadgeCheck className="w-6 h-6 text-[#0052CC]" />
        <h2 className="font-heading text-xl md:text-2xl font-bold text-gray-900">
          MPStore Recommendation Store
        </h2>
      </div>

      <div className="space-y-6">
        {stores.map((store) => (
          <div
            key={store.alias || store.store_id}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-6"
            data-testid={`rec-store-${store.alias}`}
          >
            {/* Store Header */}
            <div
              className="flex items-center justify-between mb-4 cursor-pointer"
              onClick={() => store.alias && navigate(`/merchant/${store.alias}`)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 bg-[#0052CC]/10 text-[#0052CC] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Store className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-bold text-gray-900 truncate">
                      {store.store_name}
                    </h3>
                    {store.is_open === 'open' && (
                      <span className="text-[10px] font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                        Buka
                      </span>
                    )}
                  </div>
                  {store.city && (
                    <p className="text-xs text-gray-500 truncate">{store.city}</p>
                  )}
                </div>
              </div>
              <button className="text-[#0052CC] hover:bg-[#0052CC]/10 text-sm font-semibold flex items-center gap-1 rounded-lg px-3 py-1.5 flex-shrink-0">
                Lihat Toko
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Products */}
            {store.products && store.products.length > 0 ? (
              <div
                className="flex gap-4 overflow-x-auto scroll-smooth pb-2"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {store.products.map((product, index) => (
                  <div
                    key={`${product.product_id}-${index}`}
                    className="flex-shrink-0 w-[160px] sm:w-[180px]"
                  >
                    <ProductCard product={product} merchant={store} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Belum ada produk</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecommendationStoreSection;
